'use client';
import Logo from '@/components/global/Logo';
import { Motion } from '@/components/global/utils/Motion';
import { GroqImage } from '@/helpers/Image';
import { GROQButton, GROQImage } from '@/sanity/lib/definitions';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { motion, useScroll, useTransform } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { useRef } from 'react';
import { useIsMobile } from '@/hooks/useIsMobile';
import { CTAButton } from '@/shadcn/ui/button';

interface IPriceSection {
  is3DImage?: boolean;
  imageType?: '3d' | 'static';
  image: GROQImage;
  textPreTitle: {
    firstText: string;
    secondText: string;
    secondTextBold: string;
  };
  title: any;
  price: string;
  labels: {
    icon: GROQImage;
    text: string;
  }[];
  button: GROQButton;
}

export default function PriceSection({
  imageType = '3d',
  image,
  textPreTitle,
  title,
  price,
  labels,
  button,
}: IPriceSection) {
  const is3D = imageType === '3d';

  return (
    <section className="pt-[3.25rem] pb-[20.13rem] lg:pt-[23.5rem] lg:pb-[10.13rem] overflow-clip bg-[linear-gradient(to_bottom,#F2F0EF_76.5%,white_76.5%)] lg:bg-[linear-gradient(to_bottom,#F2F0EF_72.65%,white_72.65%)]">
      <div id="price-section" className="container flex flex-col-reverse lg:flex-row items-center gap-[4rem] lg:gap-[3.25rem] justify-center w-full lg:w-fit">
        <div className="flex flex-col gap-[4rem] lg:gap-[7.5rem]">
          <motion.div
            initial={{ clipPath: 'inset(0% 0% 100% 0%)' }}
            whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1] }}
            viewport={{ once: true }}
            className="flex flex-wrap w-full justify-center lg:justify-start items-center lg:items-start gap-[3rem] lg:gap-[5rem] lg:max-w-[36.125rem]"
          >
            <motion.div className="flex w-full lg:w-[90rem] flex-col font-nunito text-[2rem] text-black leading-none text-center lg:text-left prose-h2:text-[6rem] prose-h3:text-[3rem] lg:prose-h3:text-[4rem] prose-strong:font-[600] prose-headings:uppercase">
              {textPreTitle?.firstText && (
                  <p className="mb-[1rem]"><strong>{textPreTitle?.firstText}</strong></p>
              )}
              {textPreTitle?.secondText && (
                <p className="flex flex-row items-center justify-center lg:justify-start gap-[1rem] text-[#898989] mb-[4rem] lg:mb-[5rem]">
                  {textPreTitle?.secondText} <span className="block  w-[4rem] h-[.125rem] bg-[#898989]"/>
                  <strong className="text-black">{textPreTitle?.secondTextBold}</strong>
                </p>
              )}
              {title && (
                <div className="prose-em:!font-[300]">
                  <PortableText value={title} components={richTextComponent} />
                </div>
              )}
            </motion.div>

            {price && (
              <motion.div
                initial={{ opacity: 0, y: '-1rem' }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1], delay: 0.2 }}
                className="text-black flex flex-row gap-[3rem] items-center text-center lg:text-left justify-center lg:justify-start w-full"
              >
                <h2 className="text-[4rem] lg:text-[6rem] leading-none uppercase font-nunito font-[200] text-black">
                  {price}
                </h2>
                <svg className="w-[9.49625rem] h-[2.03063rem] hidden lg:block" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 76 17" fill="none">
                  <path opacity="0.08" d="M70.3015 8.63797L75.9612 8.78271L75.9677 7.59177L70.3067 7.67063C66.6502 7.65915 63.6541 4.25763 63.4653 0.00737517L62.4756 -0.000106759C62.2588 4.25519 59.2379 7.61508 55.5814 7.60359L0.00692759 7.39373L0.000377666 8.5847L55.576 8.57096C59.2326 8.58245 62.2286 11.984 62.4175 16.2342L63.4071 16.2417C63.6239 11.9864 66.6449 8.62648 70.3015 8.63797Z" fill="black"/>
                </svg>
              </motion.div>
            )}

            <motion.div
              transition={{ duration: 1.25, ease: [0.25, 1, 0.5, 1], delay: 2 }}
              viewport={{ once: true }}
              className="flex justify-center w-full lg:w-auto"
            >
              {button && <CTAButton data={button} />}
            </motion.div>
          </motion.div>
          {labels && (
            <div className="flex flex-wrap gap-[1rem] items-center lg:items-start justify-center lg:justify-start">
              {labels.map((label, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: '-5rem' }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 1.5, ease: [0.25, 1, 0.5, 1], delay: 0.2 + index * 0.1 }}
                  className="flex flex-row gap-[1rem] items-center p-[.5rem] pr-[1rem] rounded-[.5rem] border border-black/5"
                >
                  <div className="flex items-center justify-center w-[3rem] h-[3rem] flex-shrink-0 rounded-[.5rem] border border-[#DADADA][16%]">
                    <GroqImage src={label.icon} alt={label.text} className="w-[2rem] h-[2rem] aspect-square" />
                  </div>
                  <p className="text-black/[72%] text-[2rem] font-nunito font-[300] leading-none">{label.text}</p>
                </motion.div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-col-reverse lg:flex-col items-center">
          <motion.div
            initial={{ clipPath: 'inset(0% 100% 0% 0%)' }}
            whileInView={{ clipPath: 'inset(0% 0% 0% 0%)' }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            className={`w-[30.125rem] lg:w-[44.89688rem] h-[58.25rem] lg:h-[86.87663rem] relative overflow-hidden ${is3D ? 'cursor-grab' : ''}`}
          >
            {image && (
              <GroqImage
                src={image}
                alt="Common Questions Image"
                className="!w-full !h-full !object-cover"
              />
            )}
          </motion.div>
          {is3D && (
            <div className="flex flex-row gap-[1rem] items-center">
              <p className="text-[#898989] text-[2rem] font-nunito font-[200] leading-none">Tap & Hold to Explore</p>
              <svg className="w-[2rem] h-[1.75rem]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 14" fill="none">
                <path fill-rule="evenodd" clip-rule="evenodd" d="M15.8789 6.90246L14.6815 5.54337L13.4795 4.11121C13.2265 3.85303 12.8469 3.85303 12.5984 4.11121L11.3964 5.54337L10.1944 6.90246C9.94139 7.18499 10.0679 7.38959 10.3842 7.38959H11.2021V7.52111C11.2021 9.49886 10.3164 10.9846 9.11899 11.876C7.41545 13.1669 5.08381 13.2351 3.31249 11.7445C7.03588 16.3674 14.8577 13.3715 14.8577 7.39446H15.6756C15.9919 7.39446 16.1184 7.18986 15.8654 6.90733L15.8789 6.90246ZM4.58675 8.47588L5.78872 7.04372C6.04177 6.77093 5.97399 6.56634 5.59894 6.56634H4.77654V6.43481C4.77654 4.52527 5.66672 3.02491 6.86417 2.08475C8.56771 0.857182 10.9671 0.715914 12.7294 2.21627C9.00151 -2.34813 1.11641 0.647716 1.11641 6.56634H0.298525C-0.0177831 6.56634 -0.0855634 6.77093 0.10874 7.04372L1.36945 8.47588L2.57142 9.83498C2.75669 10.1078 3.14078 10.1078 3.38478 9.83498L4.58675 8.47588Z" fill="#898989"/>
              </svg>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
